/**
 * Web-side view of the module entitlement layer.
 *
 * The authoritative list of enabled modules for the tenant comes from the API
 * (`/auth/me` → user.enabledModules). This file maps front-end routes/sidebar
 * items to their owning module and provides enablement checks used by the
 * sidebar filter and route guard.
 *
 * IMPORTANT: keep the module keys in sync with the backend registry
 * (apps/api/src/common/modules/module-registry.ts).
 */

export const CORE_MODULE_KEY = 'core';

/** All licensable (non-core) module keys, in display order. */
export const LICENSABLE_MODULES: { key: string; label: string }[] = [
  { key: 'leads', label: 'Lead Management' },
  { key: 'crm', label: 'CRM / Subscribers' },
  { key: 'billing', label: 'Billing' },
  { key: 'tickets', label: 'Ticketing' },
  { key: 'field_ops', label: 'Field Operations' },
  { key: 'olt', label: 'OLT Management' },
  { key: 'tr069', label: 'TR-069 / ACS' },
  { key: 'ipdr', label: 'IPDR / Compliance' },
  { key: 'network', label: 'Network Management' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'whatsapp', label: 'WhatsApp & Campaigns' },
  { key: 'analytics', label: 'Analytics & Reports' },
  { key: 'partners', label: 'Partner Management' },
  { key: 'api_management', label: 'API & Vendor Management' },
];

/**
 * Route-prefix → module rules. Longest matching prefix wins. A path that
 * matches no rule is treated as CORE (always available).
 */
const ROUTE_MODULE_RULES: [string, string][] = [
  ['/leads', 'leads'],
  ['/subscribers', 'crm'],
  ['/subscriber-onboarding', 'crm'],
  ['/subscribers/static-ip-requests', 'crm'],
  ['/onboarding', 'crm'],
  ['/kyc', 'crm'],
  ['/tags', 'crm'],
  ['/billing', 'billing'],
  ['/plans', 'billing'],
  ['/tickets', 'tickets'],
  ['/sla', 'tickets'],
  ['/dispatch', 'field_ops'],
  ['/olt', 'olt'],
  ['/ipdr', 'ipdr'],
  ['/compliance', 'ipdr'],
  ['/network', 'network'],
  ['/nas', 'network'],
  ['/noc', 'network'],
  ['/bandwidth', 'network'],
  ['/pppoe', 'network'],
  ['/inventory', 'inventory'],
  ['/settings/inventory', 'inventory'],
  ['/ipam', 'network'],
  ['/maintenance', 'network'],
  ['/firmware', 'network'],
  ['/radius', 'network'],
  ['/radius-manager', 'network'],
  ['/whatsapp', 'whatsapp'],
  ['/campaigns', 'whatsapp'],
  ['/settings/whatsapp', 'whatsapp'],
  ['/analytics', 'analytics'],
  ['/reports', 'analytics'],
  ['/scheduled-reports', 'analytics'],
  ['/partners', 'partners'],
  ['/settings/partner-commission', 'partners'],
  ['/api-management', 'api_management'],
  ['/api-usage', 'api_management'],
];

/** The module a given app path belongs to, or undefined for core/always-on paths. */
export function moduleForPath(path: string): string | undefined {
  let best: string | undefined;
  let bestLen = -1;
  for (const [prefix, mod] of ROUTE_MODULE_RULES) {
    if ((path === prefix || path.startsWith(prefix + '/')) && prefix.length > bestLen) {
      best = mod;
      bestLen = prefix.length;
    }
  }
  return best;
}

/**
 * Whether a module is enabled for the current tenant. `enabledModules` comes
 * from the auth store. When it is undefined (not yet loaded, or an older
 * backend that doesn't send it) we fail OPEN so the UI is never hidden by
 * accident. Core is always enabled.
 */
export function isModuleEnabled(enabledModules: string[] | undefined, moduleKey: string | undefined): boolean {
  if (!moduleKey || moduleKey === CORE_MODULE_KEY) return true;
  if (!enabledModules) return true; // fail-open until /auth/me resolves
  return enabledModules.includes(moduleKey);
}

/** Whether the module owning `path` is enabled for the current tenant. */
export function isPathModuleEnabled(enabledModules: string[] | undefined, path: string): boolean {
  return isModuleEnabled(enabledModules, moduleForPath(path));
}
